import { z } from 'zod';

import {
  AnyMindValueType,
  CompanyProbationReviewTermMode,
  ProbationReviewCompletionMethod,
  ProbationReviewFlag,
  ProbationReviewOutcome,
  ProbationReviewReviewStatus,
  ProbationStatus,
} from '@/common/graphql';

const ProbationReviewStage = z.object({
  reviewerId: z.string().default(''),
  deadline: z.string().default(''),
  reviewerFeedback: z.string().nullable().default(''),
  selfFeedback: z.string().nullable().default(''),
  reviewerName: z.string().optional().default(''),
  // The stored reviewer has left (Employee Status Inactive on the Last Working Date).
  reviewerHasLeft: z.boolean().default(false),
  // Draft, Submitted or Completed; only a Draft review can be edited.
  reviewStatus: z.nativeEnum(ProbationReviewReviewStatus).default(ProbationReviewReviewStatus.Draft),
  completionMethod: z.nativeEnum(ProbationReviewCompletionMethod).nullable().default(null),
  submittedAt: z.string().nullable().default(null),
  isExtension: z.boolean().default(false),
  extensionReason: z.string().nullable().default(null),
  needAssessments: z.boolean().default(false),
  assessments: z
    .array(
      z.object({
        assessment: z.object({
          reviewer: z.number().nullable(),
          self: z.number().nullable(),
        }),
        type: z.nativeEnum(AnyMindValueType),
      }),
    )
    .nullable()
    .default(null),
});

export const ProbationReviewDetail = z.object({
  probationStatus: z.nativeEnum(ProbationStatus).default(ProbationStatus.InProgress),
  // Set only by Record Outcome; while it is set nothing can be edited.
  outcome: z.nativeEnum(ProbationReviewOutcome).nullable().default(null),
  flags: z.array(z.nativeEnum(ProbationReviewFlag)).default([]),
  expectation: z.string().default(''),
  employeeId: z.string().default(''),
  probationEndDate: z.string().nullable().default(''),
  nextDueDate: z.string().nullable().default(null),
  probationReviewTerm: z
    .object({
      length: z.number().default(0),
      mode: z.nativeEnum(CompanyProbationReviewTermMode).default(CompanyProbationReviewTermMode.Month),
    })
    .nullable()
    .default(null),
  daysOverdue: z.number().default(0),
  reviewerAbsent: z.boolean().default(false),
  // How the probation was extended; null when it never was.
  extension: z
    .object({
      count: z.number(),
      originalEndDate: z.string(),
      newEndDate: z.string(),
      reason: z.string().nullable().default(null),
    })
    .nullable()
    .default(null),
  // The reviews of the probation (the GraphQL field is `reviews`; the draft mutations call them `stages`).
  stages: z.array(ProbationReviewStage).default([]),
});

export type ProbationReviewDetailType = z.infer<typeof ProbationReviewDetail>;
export type ProbationReviewStageType = z.infer<typeof ProbationReviewStage>;
